import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getEmailDashboard } from '../../../api/adminEmailCenter';
import EmailCenterPage, { EmptyState, PageSection, StatCard, StatusBadge } from './EmailCenterShared';

const TinyBar = ({ label, sent = 0, failed = 0 }) => {
  const total = Math.max(sent + failed, 1);
  return (
    <div className="space-y-2 rounded-2xl border border-slate-100 bg-slate-50/60 p-3">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="text-slate-500">{sent + failed}</span>
      </div>
      <div className="flex h-2 overflow-hidden rounded-full bg-slate-200">
        <div className="bg-emerald-500" style={{ width: `${(sent / total) * 100}%` }} />
        <div className="bg-rose-500" style={{ width: `${(failed / total) * 100}%` }} />
      </div>
    </div>
  );
};

export default function EmailDashboard() {
  const [state, setState] = useState({ loading: true, error: '', data: null });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getEmailDashboard();
        if (!active) return;
        setState({ loading: false, error: '', data });
      } catch (error) {
        if (!active) return;
        setState({ loading: false, error: error.message || 'Failed to load email dashboard', data: null });
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const dashboard = state.data;

  return (
    <EmailCenterPage
      title="Operational Email Command Center"
      description="Track delivery health, campaign activity, SMTP readiness, and the most recent failures from one place."
      actions={
        <>
          <Link to="/admin/email/campaigns/new" className="rounded-full bg-emerald-600 px-5 py-3 text-sm font-semibold text-white shadow-[0_14px_32px_rgba(15,118,110,0.24)]">
            New Campaign
          </Link>
          <Link to="/admin/email/settings" className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700">
            SMTP Settings
          </Link>
        </>
      }
    >
      {state.error ? (
        <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">{state.error}</div>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-5 md:grid-cols-2">
        <StatCard label="Sent Today" value={dashboard?.cards?.sentToday ?? (state.loading ? '...' : 0)} hint="Successful deliveries since midnight." tone="emerald" />
        <StatCard label="Failed Today" value={dashboard?.cards?.failedToday ?? (state.loading ? '...' : 0)} hint="Needs queue retry or content review." tone="rose" />
        <StatCard label="Queue Pending" value={dashboard?.cards?.queuePending ?? (state.loading ? '...' : 0)} hint="Queued + processing messages." tone="amber" />
        <StatCard label="Active Campaigns" value={dashboard?.cards?.activeCampaigns ?? (state.loading ? '...' : 0)} hint="Draft, scheduled, processing, paused." tone="sky" />
        <StatCard label="Success Rate" value={`${dashboard?.cards?.deliverySuccessRate ?? (state.loading ? '...' : 0)}%`} hint="Based on sent vs failed messages." />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.3fr_0.7fr]">
        <PageSection title="Recent Campaigns" subtitle="Live campaign activity with queue health built into each row.">
          {!dashboard?.recentCampaigns?.length && !state.loading ? (
            <EmptyState title="No campaigns yet" body="Create your first campaign to start building marketing and lifecycle communication history." />
          ) : (
            <div className="space-y-3">
              {(dashboard?.recentCampaigns || []).map((campaign) => (
                <div key={campaign.id} className="flex flex-col gap-3 rounded-[24px] border border-slate-100 p-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-lg font-semibold text-slate-900">{campaign.campaign_name}</p>
                    <p className="mt-1 text-sm text-slate-500">
                      queued {campaign.queued_count || 0} | sent {campaign.sent_count || 0} | failed {campaign.failed_count || 0}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge value={campaign.status} />
                    <Link to={`/admin/email/campaigns/${campaign.id}`} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700">
                      Open
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </PageSection>

        <PageSection title="SMTP Health" subtitle="Current backend transport state and the most recent test result.">
          <div className="space-y-3">
            <div className="rounded-[24px] border border-slate-100 bg-slate-50/60 p-4">
              <p className="text-sm font-semibold text-slate-900">SMTP Configured</p>
              <p className="mt-2 text-sm text-slate-500">{dashboard?.smtpStatus?.smtp?.configured ? 'Yes, transport can be used by campaigns and transactional mail.' : 'SMTP configuration is incomplete.'}</p>
            </div>
            <div className="rounded-[24px] border border-slate-100 bg-slate-50/60 p-4">
              <p className="text-sm font-semibold text-slate-900">Last Test</p>
              <p className="mt-2 text-sm text-slate-500">
                {dashboard?.smtpStatus?.lastTest?.status
                  ? `${dashboard.smtpStatus.lastTest.status} at ${dashboard.smtpStatus.lastTest.at || 'N/A'}`
                  : 'No admin test email has been sent yet.'}
              </p>
            </div>
            <Link to="/admin/email/settings" className="inline-flex rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white">
              Open Settings
            </Link>
          </div>
        </PageSection>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <PageSection title="Last 7 Days" subtitle="Quick delivery trend of sent vs failed messages.">
          {!dashboard?.last7Days?.length && !state.loading ? (
            <EmptyState title="No trend data yet" body="Once emails start flowing, weekly trend cards will appear here." />
          ) : (
            <div className="space-y-3">
              {(dashboard?.last7Days || []).map((row) => (
                <TinyBar key={row.day} label={row.day} sent={row.sent} failed={row.failed} />
              ))}
            </div>
          )}
        </PageSection>

        <div className="grid gap-6">
          <PageSection title="Recent Failures" subtitle="Most recent errors for quick triage.">
            {!dashboard?.recentFailures?.length && !state.loading ? (
              <EmptyState title="No recent failures" body="Delivery failures will show up here with direct links into mail logs." />
            ) : (
              <div className="space-y-3">
                {(dashboard?.recentFailures || []).map((row) => (
                  <div key={row.id} className="rounded-[24px] border border-rose-100 bg-rose-50/50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{row.recipient_email}</p>
                        <p className="mt-1 text-sm text-slate-500">{row.subject}</p>
                      </div>
                      <Link to={`/admin/email/logs?search=${encodeURIComponent(row.recipient_email)}`} className="rounded-full border border-rose-200 px-4 py-2 text-xs font-semibold text-rose-700">
                        View Logs
                      </Link>
                    </div>
                    <p className="mt-3 text-sm text-rose-700">{row.failure_reason || 'Unknown provider failure'}</p>
                  </div>
                ))}
              </div>
            )}
          </PageSection>

          <PageSection title="Usage Mix" subtitle="Top templates and message categories currently in use.">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-[24px] border border-slate-100 p-4">
                <p className="text-sm font-semibold text-slate-900">Top Templates</p>
                <div className="mt-3 space-y-3">
                  {(dashboard?.topTemplates || []).map((row) => (
                    <div key={row.name} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{row.name || 'Untitled'}</span>
                      <span className="font-semibold text-slate-900">{row.total}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-[24px] border border-slate-100 p-4">
                <p className="text-sm font-semibold text-slate-900">Message Types</p>
                <div className="mt-3 space-y-3">
                  {(dashboard?.messageTypeBreakdown || []).map((row) => (
                    <div key={row.type} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{row.type}</span>
                      <span className="font-semibold text-slate-900">{row.total}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </PageSection>
        </div>
      </div>
    </EmailCenterPage>
  );
}
